import {
  Box,
  Button,
  ClickAwayListener,
  Drawer,
  Popper,
  useMediaQuery,
} from '@mui/material';
import { FC, useCallback, useRef, useState } from 'react';

import { BackIcon, DateRangePicker } from '@abg/kit-base';

import { useDateTimePickerController } from './hooks/use-date-time-picker-controller';
import { DateTimePickerErrorBanner } from './molecules/DateTimePickerErrorBanner';
import { DateTimePickerFooter } from './molecules/DateTimePickerFooter';
import {
  bookingWidgetDateTimePopperStyles,
  bookingWidgetFullScreenCalendarStyles,
  bookingWidgetFullScreenDrawerStyles,
  bookingWidgetFullScreenFooterStyles,
  bookingWidgetFullScreenPaperStyles,
  bookingWidgetFullScreenRootStyles,
  dateTimePopperContainerStyles,
} from './DateTimePickerDesktop.styling';
import {
  backIconStyles,
  drawerIconContainerStyles,
} from './DateTimePickerMobile.styling';
import {
  DateTimePickerErrorEnum,
  DateTimePickerProperties,
  InitialDraftStateT,
} from './types';

const VIEWPORT_GAP_PX = 16;
const FULL_SCREEN_MONTHS = 8;

export const FULL_SCREEN_CALENDAR_MAX_WIDTH_PX = 1280;
export const FULL_SCREEN_CALENDAR_MAX_HEIGHT_PX = 800;

export const FULL_SCREEN_CALENDAR_QUERY = `(max-width: ${FULL_SCREEN_CALENDAR_MAX_WIDTH_PX}px) and (max-height: ${FULL_SCREEN_CALENDAR_MAX_HEIGHT_PX}px)`;

export const shouldOpenFullScreenCalendar = (
  width: number,
  height: number,
): boolean =>
  width <= FULL_SCREEN_CALENDAR_MAX_WIDTH_PX &&
  height <= FULL_SCREEN_CALENDAR_MAX_HEIGHT_PX;

export const BookingWidgetDateTimePickerDesktop: FC<
  DateTimePickerProperties
> = ({
  isOpen,
  anchorRef,
  onClose,
  onDone,
  uiTextData,
  timePickerOptions,
  pickUpOpeningHours = [],
  pickupHolidays = [],
  dropoffOpeningHours = [],
  dropoffHolidays = [],
  dropoff24hrsFlag,
  dateRangePickerProps,
  initialDraftState,
  maxBookingPeriod,
  brand,
  weekStartsOn,
  locale,
  timeFormat,
  dateType,
}) => {
  const isFullScreenViewport = useMediaQuery(FULL_SCREEN_CALENDAR_QUERY);
  const fullScreenContainerReference = useRef<HTMLDivElement>(null);

  const {
    draftValues,
    nonWorkingPickupHolidays,
    nonWorkingDropoffHolidays,
    todayAsUnavailableDay,
    handleValueChange,
    daysSelected,
    handleCancel,
    handleDone,
    dropoffLocationTimeOptions,
    pickUpLocationClosedDays,
    dropoffLocationClosedDays,
    showDateTimePickerError,
  } = useDateTimePickerController({
    onClose,
    onDone,
    uiTextData,
    timePickerOptions,
    pickUpOpeningHours,
    pickupHolidays,
    dropoffOpeningHours,
    dropoffHolidays,
    dropoff24hrsFlag,
    initialDraftState,
    timeFormat,
    maxBookingPeriod,
  });

  const isDropoffTimeUnreachable = dropoffLocationTimeOptions.length === 0;
  const clickCountReference = useRef(0);
  const [isSelectingDropoffInPickup, setIsSelectingDropoffInPickup] =
    useState(false);
  const isDropoffCalendar = dateType === 'dropoff';

  const resetSelection = useCallback(() => {
    clickCountReference.current = 0;
    setIsSelectingDropoffInPickup(false);
  }, []);

  const handleDatesChange = useCallback(
    (dates: InitialDraftStateT['dates']) => {
      handleValueChange('dates', dates);

      if (!dates?.from) return;

      if (isDropoffCalendar) {
        const selectedDropoffDate = dates.to || dates.from;
        const pickupDate = initialDraftState.dates?.from;

        if (pickupDate && selectedDropoffDate < pickupDate) {
          clickCountReference.current = 1;
          onDone({
            dates: { from: selectedDropoffDate, to: undefined },
            pickupTime: draftValues.pickupTime,
            dropoffTime: draftValues.dropoffTime,
          });
          return;
        }

        onDone({
          dates: { from: pickupDate, to: selectedDropoffDate },
          pickupTime: draftValues.pickupTime,
          dropoffTime: draftValues.dropoffTime,
        });
        onClose();
        return;
      }

      clickCountReference.current += 1;

      if (clickCountReference.current === 1) {
        setIsSelectingDropoffInPickup(true);
        onDone({
          dates: { from: dates.from, to: undefined },
          pickupTime: draftValues.pickupTime,
          dropoffTime: draftValues.dropoffTime,
        });
        return;
      }

      if (clickCountReference.current === 2) {
        if (
          dates.from &&
          dates.to &&
          dates.from.getTime() === dates.to.getTime() &&
          draftValues.dates?.from &&
          dates.from < draftValues.dates.from
        ) {
          clickCountReference.current = 1;
          onDone({
            dates: { from: dates.from, to: undefined },
            pickupTime: draftValues.pickupTime,
            dropoffTime: draftValues.dropoffTime,
          });
          return;
        }

        onDone({
          dates: { from: dates.from, to: dates.to || dates.from },
          pickupTime: draftValues.pickupTime,
          dropoffTime: draftValues.dropoffTime,
        });
        resetSelection();
        onClose();
      }
    },
    [
      draftValues.dates?.from,
      draftValues.dropoffTime,
      draftValues.pickupTime,
      handleValueChange,
      initialDraftState.dates?.from,
      isDropoffCalendar,
      onClose,
      onDone,
      resetSelection,
    ],
  );

  if (!isOpen) {
    return null;
  }

  const calendar = (
    <DateRangePicker
      value={draftValues.dates}
      weekStartsOn={weekStartsOn || 0}
      locale={locale}
      minDateValue={dateRangePickerProps.dateRangePickerMinDateValue}
      maxDateValue={dateRangePickerProps.dateRangePickerMaxDateValue}
      maxRentalDays={
        isDropoffCalendar || isSelectingDropoffInPickup
          ? maxBookingPeriod
          : undefined
      }
      maxRentalStartDate={
        isDropoffCalendar
          ? initialDraftState.dates?.from
          : isSelectingDropoffInPickup
            ? draftValues.dates?.from
            : initialDraftState.dates?.from
      }
      isSelectingDropoff={isDropoffCalendar}
      onChange={handleDatesChange}
      pickUpLocationHolidays={[
        todayAsUnavailableDay,
        ...nonWorkingPickupHolidays,
      ]}
      pickUpLocationClosedDays={pickUpLocationClosedDays}
      dropOffLocationHolidays={
        dropoff24hrsFlag ? [] : nonWorkingDropoffHolidays
      }
      dropOffLocationClosedDays={
        dropoff24hrsFlag ? [] : dropoffLocationClosedDays
      }
      {...(isFullScreenViewport
        ? {
            numberOfMonths: FULL_SCREEN_MONTHS,
            hideNavigation: true,
            isScrollable: true,
            containerRef: fullScreenContainerReference,
          }
        : {})}
    />
  );

  const errorBanner = showDateTimePickerError ? (
    <DateTimePickerErrorBanner
      showDateTimePickerError={
        showDateTimePickerError as DateTimePickerErrorEnum
      }
      maxBookingPeriod={maxBookingPeriod}
    />
  ) : null;

  const footer = (
    <DateTimePickerFooter
      brand={brand}
      isDropoffTimeUnreachable={isDropoffTimeUnreachable}
      uiTextData={uiTextData}
      daysSelected={daysSelected}
      showDateTimePickerError={showDateTimePickerError}
      onClose={handleCancel}
      onDone={handleDone}
    />
  );

  if (isFullScreenViewport) {
    return (
      <Drawer
        open={isOpen}
        onClose={handleCancel}
        anchor="right"
        slotProps={{
          root: {
            sx: bookingWidgetFullScreenDrawerStyles,
          },
          paper: {
            sx: bookingWidgetFullScreenPaperStyles,
          },
        }}
      >
        <Box
          ref={fullScreenContainerReference}
          sx={bookingWidgetFullScreenRootStyles}
          data-testid="fullScreenCalendar"
        >
          <Box sx={drawerIconContainerStyles}>
            <Button
              aria-label={uiTextData.buttonCancelTitle}
              onClick={handleCancel}
              variant="text"
              sx={backIconStyles}
            >
              <BackIcon />
            </Button>
          </Box>

          <Box sx={bookingWidgetFullScreenCalendarStyles}>
            {calendar}
            {errorBanner}
          </Box>

          <Box sx={bookingWidgetFullScreenFooterStyles}>{footer}</Box>
        </Box>
      </Drawer>
    );
  }

  return (
    <Popper
      open={isOpen}
      anchorEl={anchorRef.current}
      placement="bottom-end"
      disablePortal
      sx={bookingWidgetDateTimePopperStyles}
      modifiers={[
        {
          name: 'flip',
          enabled: false,
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            boundary: 'viewport',
            padding: VIEWPORT_GAP_PX,
            altAxis: true,
          },
        },
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ]}
    >
      <ClickAwayListener
        onClickAway={() => {
          resetSelection();
          onClose();
        }}
      >
        <Box
          sx={dateTimePopperContainerStyles(brand)}
          data-testid="dateTimePicker"
        >
          {calendar}
          {errorBanner}
          {footer}
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};
